import { type ModalWidth } from "components/modal";
import { useValidateIsNotEmptyArray } from "formvalidation/hooks";
import React, { useRef } from "react";
import { MultiModalPickerProps } from "./props";
import { SelectOrNew } from "./SelectOrNew";
import { ModalForMultiPicker } from "./ModalForMultiPicker";
import { RemovableLabelList } from "./RemovableLabelList";

interface Props<T> extends MultiModalPickerProps<T> {
  buttonLabel: string
  modalTitle: string
  modalSize: ModalWidth

  onNewClicked: () => void

  renderModalContent: (
    editedValue: T[],
    setEditedValue: (value: T[]) => void,
  ) => React.ReactNode
  renderLabelContent: (value: T) => string | React.ReactNode
  getId: (value: T) => string | number;
}

const MultiModalPickerWithChooseButton = <T extends unknown>(props: Props<T>) => {
  const { canCreateNew = false } = props;

  const [isModalOpen, setIsModalOpen] = React.useState(props.inPageEditing ?? false);

  const onSave = (newValue: T[]) => {
    props.onChange(newValue);
    setIsModalOpen(false);
  }

  const ref = useRef<HTMLDivElement>(null);

  let hasError: boolean = false;

  if (props.required) {
    const validation = useValidateIsNotEmptyArray({
      key: props.validatorKey,
      title: props.validatorKey,
      value: props.value,
      ref
    });

    hasError = validation.error.hasError;
  }

  const removeItem = (item: T) => {
    props.onChange(props.value.filter(i => props.getId(i) !== props.getId(item)));
  }

  return <>
    <div ref={ref}></div>
    {!props.inPageEditing && <div className="flex gap-large">
      <SelectOrNew
        canCreateNew={canCreateNew}
        newButton={{
          label: RESX.Buttons.btnNew,
          onClick: props.onNewClicked
        }}
        selectButton={{
          label: props.buttonLabel,
          onClick: () => setIsModalOpen(true)
        }}
        hasError={hasError}
      />

      <RemovableLabelList
        value={props.value}
        renderLabelContent={props.renderLabelContent}
        getId={props.getId}
        removeItem={removeItem}
      />
    </div>}

    {isModalOpen && <ModalForMultiPicker
      buttonLabel={props.buttonLabel}
      modalTitle={props.modalTitle}
      modalSize={props.modalSize}
      onClose={() => {
        setIsModalOpen(false)
        props.inPageEditing?.cancel()
      }}
      onSave={onSave}
      initialValue={props.value}
      renderModalContent={props.renderModalContent}
    />}
  </>;
};

export default MultiModalPickerWithChooseButton;